import React, {
  useRef,
  useCallback,
  useContext,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';

import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import ClientDocument from '../../../../../chat/Model/ClientDocument';
import useStorage, { StorageProcessState } from '../../../../../chat/hooks/useStorage';
import { storagePaths, pathPlaceholder } from '../../../../../utils/firebasePaths';
import { cleanAttachmentTitle } from '../../../../../utils/attachments';
import User from '../../../../../Model/User';
import useToast from '../../../../hooks/useToast';
import LoadingOverlay from '../../../LoadingOverlay';
import ClientDocumentsPanel from '../../../../../components/ClientDocumentsPanel';
import { PrimaryButton } from '../../../../../components/Button/ActionButtons';
import { ReactComponent as UploadIcon } from '../../../../../assets/icons/v2/creation-plus-circle.svg';

import { PanelHeader, PanelTitle, Container } from './styles';
import texts from './texts.json';

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;

const ClientDocuments = ({ userDoc, coachId }) => {
  const { userDoc: loggedInUserDoc } = useContext(LoggedInUserContext);
  const { uploadAttachments } = useStorage();
  const { showToast } = useToast();
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFilesSelected = useCallback(async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const validFiles = files.filter((f) => f.size <= MAX_FILE_SIZE_BYTES);
    const skippedCount = files.length - validFiles.length;
    if (skippedCount > 0) {
      showToast(format(texts.oversizedSkipped, { count: skippedCount }), { error: true });
    }

    if (!validFiles.length) return;

    const uploaderName = loggedInUserDoc?.name || loggedInUserDoc?.email || '';
    const uploaderId = loggedInUserDoc?.id || '';
    const uploadPath = storagePaths.CLIENT_DOCUMENTS_COLLECTION
      .replace(`{${pathPlaceholder.USER_ID}}`, userDoc?.id);

    const filesToUpload = validFiles.map((file) => ({
      dataUrl: URL.createObjectURL(file),
      format: file.name.split('.').pop(),
      fileName: file.name.split('.').slice(0, -1).join('.'),
      fileType: file.type,
    }));

    setIsUploading(true);
    try {
      const results = await uploadAttachments(filesToUpload, uploadPath);

      await Promise.all(results.map((result) => {
        if (result.state !== StorageProcessState.SUCCESS) return Promise.resolve();
        // Describe what was actually stored, not what was picked: a HEIC upload is
        // converted to JPEG on the way, so the original file's name, size and type no
        // longer match the object this document points at.
        return ClientDocument.addDoc({
          assetPath: result.fileRef,
          fileName: cleanAttachmentTitle(result.metadata.name),
          fileSize: result.metadata.size,
          mimeType: result.metadata.contentType,
          uploadedBy: uploaderId,
          uploadedByName: uploaderName,
          coachId,
          userId: userDoc?.id,
        });
      }));
      showToast(texts.uploadSuccess);
    } catch {
      showToast(texts.uploadFailed, { error: true });
    } finally {
      filesToUpload.forEach(({ dataUrl }) => URL.revokeObjectURL(dataUrl));
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [loggedInUserDoc, userDoc, coachId, uploadAttachments, showToast]);

  return (
    <Container>
      <PanelHeader>
        <PanelTitle>{texts.title}</PanelTitle>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          id="client-doc-upload-input"
          onChange={handleFilesSelected}
        />
        <PrimaryButton
          icon={<UploadIcon />}
          size="medium"
          disabled={isUploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {isUploading ? texts.uploadingButton : texts.uploadButton}
        </PrimaryButton>
      </PanelHeader>
      <LoadingOverlay isLoading={isUploading} />
      <ClientDocumentsPanel
        userId={userDoc?.id}
        coachId={coachId}
        onlySaved
      />
    </Container>
  );
};

ClientDocuments.propTypes = {
  userDoc: PropTypes.instanceOf(User).isRequired,
  coachId: PropTypes.string.isRequired,
};

export default ClientDocuments;
